import { apiFetch } from '@/auth/api';
import { readXsrfToken } from '@/auth/csrf';

export type PresenceStatus = 'here' | 'away';

export interface PresencePeer {
  id: string;
  name: string;
  /** App pathname only (the server strips query + hash). */
  path: string;
  status: PresenceStatus;
  /** ISO. For an away peer, when they last used any Amassa tab. */
  lastActiveAt: string;
  photoUrl: string | null;
}

export interface PresenceRoster {
  selfId: string;
  /** Everyone present, self included, here before away. */
  peers: PresencePeer[];
}

export interface PresenceBeat {
  tabId: string;
  path: string;
  active: boolean;
}

/** Record this tab and get the roster back in the same round-trip. */
export function sendPresenceBeat(beat: PresenceBeat, signal?: AbortSignal): Promise<PresenceRoster> {
  return apiFetch<PresenceRoster>(
    '/presence/beat',
    { method: 'POST', body: JSON.stringify(beat), signal },
    { mutation: true },
  );
}

/**
 * Fire-and-forget from `pagehide`. `keepalive` lets the request outlive the
 * page, and unlike `sendBeacon` it can carry the XSRF header the API
 * requires on every POST.
 */
export function sendPresenceLeave(tabId: string): void {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const xsrf = readXsrfToken();
    if (xsrf) headers['X-XSRF-TOKEN'] = xsrf;
    void fetch('/api/presence/leave', {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers,
      body: JSON.stringify({ tabId }),
    }).catch(() => {
      /* the tab ages out server-side anyway */
    });
  } catch {
    /* ditto */
  }
}
